import { useEffect, useState } from 'react';
import {
  getSalesforceMessagingState,
  subscribeToSalesforceMessagingState,
  type SalesforceMessagingState,
} from '../embedded-chat';

/**
 * React hook that exposes the high-level Salesforce Messaging state.
 *
 * This hook:
 * - Subscribes to the shared, framework-agnostic messaging state observable
 * - Returns a `SalesforceMessagingState` snapshot that updates as events fire
 *
 * IMPORTANT: This hook does **not** initialize Salesforce Messaging itself.
 * Call `useInitSalesforceMessaging(enabled)` (or `initSalesforceMessaging`)
 * separately at a higher level to avoid duplicate bootstrap calls.
 *
 * Typical usage:
 * - Drive custom chat launcher visibility (business hours, readiness)
 * - Show unread message badges
 * - React to session lifecycle changes (Waiting, Active, Ended)
 */
export const useSalesforceMessagingState = (
  enabled = true,
): SalesforceMessagingState => {
  // Seed React state with the current observable snapshot so callers see
  // up-to-date values on first render.
  const [state, setState] = useState<SalesforceMessagingState>(
    getSalesforceMessagingState,
  );

  useEffect(() => {
    if (!enabled) {
      return () => {};
    }

    const unsubscribe = subscribeToSalesforceMessagingState(setState);
    return unsubscribe;
  }, [enabled]);

  return state;
};
